import type { MapMarker } from './Map';
import arrowSvg from '../../../assets/map/stops/arrow.svg';
import busActiveSvg from '../../../assets/map/stops/bus-active.svg';
import busSvg from '../../../assets/map/stops/bus.svg';
import ferryActiveSvg from '../../../assets/map/stops/ferry-active.svg';
import ferrySvg from '../../../assets/map/stops/ferry.svg';
import railActiveSvg from '../../../assets/map/stops/rail-active.svg';
import railSvg from '../../../assets/map/stops/rail.svg';
import tramActiveSvg from '../../../assets/map/stops/tram-active.svg';
import tramSvg from '../../../assets/map/stops/tram.svg';
import { getStopMarkerMode } from '../../../transportModes';
import { createSelectableMarkerRoot } from './createSelectableMarkerRoot';
import styles from './Map.module.css';

export interface StopMarkerProps {
  id: string;
  coordinate: [number, number];
  name?: string;
  kind?: Extract<MapMarker['kind'], 'stop' | 'journey-stop'>;
  mode?: string;
  bearing?: string;
}

export function createStopMarker({
  id,
  coordinate,
  name,
  kind = 'stop',
  mode,
  bearing,
}: StopMarkerProps): MapMarker {
  return {
    id,
    coordinate,
    name,
    kind,
    mode,
    bearing,
  };
}

type StopMarkerKind = Extract<MapMarker['kind'], 'stop' | 'journey-stop'>;
type NormalizedStopType = 'bus' | 'ferry' | 'rail' | 'tram';
type Bearing = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW';

const STOP_MARKER_TYPES: Record<
  NormalizedStopType,
  { default: string; active: string }
> = {
  bus: {
    default: busSvg,
    active: busActiveSvg,
  },
  ferry: {
    default: ferrySvg,
    active: ferryActiveSvg,
  },
  rail: {
    default: railSvg,
    active: railActiveSvg,
  },
  tram: {
    default: tramSvg,
    active: tramActiveSvg,
  },
};

const VALID_BEARINGS = new Set<Bearing>([
  'N',
  'NE',
  'E',
  'SE',
  'S',
  'SW',
  'W',
  'NW',
]);

export function isStopMarkerKind(
  kind: MapMarker['kind'],
): kind is StopMarkerKind {
  return kind === 'stop' || kind === 'journey-stop' || kind === undefined;
}

export function normalizeStopMarkerType(mode?: string): NormalizedStopType {
  return getStopMarkerMode(mode);
}

export function normalizeBearing(bearing?: string): Bearing | null {
  const normalizedBearing = bearing?.trim().toUpperCase() as
    | Bearing
    | undefined;

  if (!normalizedBearing || !VALID_BEARINGS.has(normalizedBearing)) {
    return null;
  }

  return normalizedBearing;
}

function createMarkerRoot(
  marker: MapMarker,
  onSelect?: (markerId: string) => void,
): HTMLButtonElement | HTMLDivElement {
  return createSelectableMarkerRoot({
    markerId: marker.id,
    ariaLabel: marker.name ?? 'Stop marker',
    className: styles.stopMarkerButton,
    onSelect,
  });
}

export function createStopMarkerElement(
  marker: MapMarker,
  onSelect?: (markerId: string) => void,
): HTMLButtonElement | HTMLDivElement {
  const root = createMarkerRoot(marker, onSelect);
  const stopType = normalizeStopMarkerType(marker.mode);
  const bearing = normalizeBearing(marker.bearing);
  const markerAssets = STOP_MARKER_TYPES[stopType];

  const markerBody = document.createElement('span');
  markerBody.className = styles.stopMarker;
  markerBody.dataset.stopType = stopType;

  const arrow = document.createElement('img');
  arrow.className = styles.stopMarkerArrow;
  arrow.src = arrowSvg;
  arrow.alt = '';
  arrow.setAttribute('aria-hidden', 'true');
  if (bearing) {
    arrow.dataset.bearing = bearing;
  } else {
    arrow.hidden = true;
  }

  const shape = document.createElement('span');
  shape.className = styles.stopMarkerShape;

  const defaultShape = document.createElement('img');
  defaultShape.className = styles.stopMarkerShapeDefault;
  defaultShape.src = markerAssets.default;
  defaultShape.alt = '';
  defaultShape.setAttribute('aria-hidden', 'true');

  const activeShape = document.createElement('img');
  activeShape.className = styles.stopMarkerShapeActive;
  activeShape.src = markerAssets.active;
  activeShape.alt = '';
  activeShape.setAttribute('aria-hidden', 'true');

  const pin = document.createElement('span');
  pin.className = styles.stopMarkerPin;
  pin.setAttribute('aria-hidden', 'true');

  shape.appendChild(defaultShape);
  shape.appendChild(activeShape);
  markerBody.appendChild(arrow);
  markerBody.appendChild(shape);
  markerBody.appendChild(pin);
  root.appendChild(markerBody);

  return root;
}
